import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  query,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../../firebase';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProviders: 0,
    totalLearners: 0,
    totalConversations: 0,
    totalMessages: 0,
    totalSupportCases: 0,
    totalSessions: 0,
    completedSessions: 0,
    totalRevenue: 0,
    recentUsers: [],
    recentSupportCases: [],
    recentSessions: []
  });

  useEffect(() => {
    if (currentUser) {
      checkAdminStatus();
    } else {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  const checkAdminStatus = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists() && userDoc.data().isAdmin) {
        setIsAdmin(true);
        fetchDashboardStats();
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      navigate('/');
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const [
        usersSnapshot,
        conversationsSnapshot,
        messagesSnapshot,
        supportCasesSnapshot,
        sessionsSnapshot
      ] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'conversations')),
        getDocs(collection(db, 'messages')),
        getDocs(collection(db, 'supportCases')),
        getDocs(collection(db, 'sessions'))
      ]);

      // Count users by type
      let totalProviders = 0;
      let totalLearners = 0;
      const recentUsers = [];

      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.userType === 'provider' || userData.userType === 'both') {
          totalProviders++;
        }
        if (userData.userType === 'learner' || userData.userType === 'both') {
          totalLearners++;
        }
        
        // Add to recent users (first 5)
        if (recentUsers.length < 5) {
          recentUsers.push({
            id: doc.id,
            ...userData
          });
        }
      });

      // Get recent support cases
      const recentSupportCases = [];
      supportCasesSnapshot.forEach((doc) => {
        if (recentSupportCases.length < 5) {
          recentSupportCases.push({
            id: doc.id,
            ...doc.data()
          });
        }
      });

      // Calculate session statistics
      let completedSessions = 0;
      let totalRevenue = 0;
      const recentSessions = [];
      
      sessionsSnapshot.forEach((doc) => {
        const sessionData = doc.data();
        
        if (sessionData.status === 'completed') {
          completedSessions++;
          totalRevenue += sessionData.amount || 0;
        }
        
        // Add to recent sessions (first 5)
        if (recentSessions.length < 5) {
          recentSessions.push({
            id: doc.id,
            ...sessionData
          });
        }
      });

      setStats({
        totalUsers: usersSnapshot.size,
        totalProviders,
        totalLearners,
        totalConversations: conversationsSnapshot.size,
        totalMessages: messagesSnapshot.size,
        totalSupportCases: supportCasesSnapshot.size,
        totalSessions: sessionsSnapshot.size,
        completedSessions,
        totalRevenue,
        recentUsers,
        recentSupportCases,
        recentSessions
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading admin dashboard...</div>;
  }

  if (!isAdmin) {
    return <div className="error">Access denied. Admin privileges required.</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h2>Admin Dashboard</h2>
        <p>System overview and management</p>
      </div>

      <div className="admin-stats">
        <h3>System Statistics</h3>
        <div className="stats-grid">
          <div className="stat-card users">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalUsers}</div>
              <div className="stat-label">Total Users</div>
            </div>
          </div>

          <div className="stat-card providers">
            <div className="stat-icon">👨‍🏫</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalProviders}</div>
              <div className="stat-label">Providers</div>
            </div>
          </div>

          <div className="stat-card learners">
            <div className="stat-icon">🎓</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalLearners}</div>
              <div className="stat-label">Learners</div>
            </div>
          </div>

          <div className="stat-card conversations">
            <div className="stat-icon">💬</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalConversations}</div>
              <div className="stat-label">Conversations</div>
            </div>
          </div>

          <div className="stat-card messages">
            <div className="stat-icon">📨</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalMessages}</div>
              <div className="stat-label">Messages</div>
            </div>
          </div>

          <div className="stat-card support">
            <div className="stat-icon">🎫</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalSupportCases}</div>
              <div className="stat-label">Support Cases</div>
            </div>
          </div>

          <div className="stat-card sessions">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalSessions}</div>
              <div className="stat-label">Total Sessions</div>
            </div>
          </div>

          <div className="stat-card completed">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-value">{stats.completedSessions}</div>
              <div className="stat-label">Completed Sessions</div>
            </div>
          </div>

          <div className="stat-card revenue">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <div className="stat-value">${stats.totalRevenue.toLocaleString()}</div>
              <div className="stat-label">Total Revenue</div>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-sections">
        <div className="admin-section">
          <div className="section-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="quick-actions">
            <button 
              className="action-btn users" 
              onClick={() => navigate('/admin/users')}
            >
              <span className="action-icon">👥</span>
              <span className="action-label">Manage Users</span>
            </button>
            
            <button 
              className="action-btn conversations" 
              onClick={() => navigate('/admin/conversations')}
            >
              <span className="action-icon">💬</span>
              <span className="action-label">View Conversations</span>
            </button>
            
            <button 
              className="action-btn support" 
              onClick={() => navigate('/admin/support-cases')}
            >
              <span className="action-icon">🎫</span>
              <span className="action-label">Support Cases</span>
            </button>
            
            <button 
              className="action-btn sessions" 
              onClick={() => navigate('/admin/sessions')}
            >
              <span className="action-icon">📅</span>
              <span className="action-label">Manage Sessions</span>
            </button>
          </div>
        </div>

        <div className="admin-section">
          <div className="section-header">
            <h3>Recent Users</h3>
            <button 
              className="view-all-btn" 
              onClick={() => navigate('/admin/users')}
            >
              View All
            </button>
          </div>
          <div className="recent-list">
            {stats.recentUsers.length === 0 ? (
              <p className="no-data">No users found</p>
            ) : (
              stats.recentUsers.map(user => (
                <div key={user.id} className="recent-item">
                  <div className="item-avatar">
                    {user.profilePicture ? (
                      <img src={user.profilePicture} alt={user.firstName} />
                    ) : (
                      <div className="default-avatar">
                        {user.firstName?.[0]}{user.lastName?.[0]}
                      </div>
                    )}
                  </div>
                  <div className="item-details">
                    <div className="item-name">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="item-meta">
                      {user.userType} • {user.email}
                    </div>
                  </div>
                  <div className="item-status">
                    {user.isProfileComplete ? (
                      <span className="status active">Active</span>
                    ) : (
                      <span className="status incomplete">Incomplete</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="admin-section">
          <div className="section-header">
            <h3>Recent Support Cases</h3>
            <button 
              className="view-all-btn" 
              onClick={() => navigate('/admin/support-cases')}
            >
              View All
            </button>
          </div>
          <div className="recent-list">
            {stats.recentSupportCases.length === 0 ? (
              <p className="no-data">No support cases found</p>
            ) : (
              stats.recentSupportCases.map(supportCase => (
                <div key={supportCase.id} className="recent-item">
                  <div className="item-icon">
                    🎫
                  </div>
                  <div className="item-details">
                    <div className="item-name">
                      {supportCase.subject}
                    </div>
                    <div className="item-meta">
                      {supportCase.category} • {supportCase.priority}
                    </div>
                  </div>
                  <div className="item-status">
                    <span className={`status ${supportCase.status?.toLowerCase()}`}>
                      {supportCase.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;